import { Bullets, Code, HelpSection, Note, P, Screen, SeeAlso, Strong, Terms } from '../prose';

/**
 * レポートの数字の意味。
 *
 * 「なぜこの数になるのか」だけを書く。計算式そのものは集計の view に 1 箇所だけ
 * あるもので、ここに写すと必ずどちらかが古くなる。
 */
export function ReadingReportsArticle() {
  return (
    <>
      <HelpSection heading="2 つのレポート">
        <Terms
          rows={[
            {
              term: 'アサイン・稼働',
              value: (
                <>
                  <Screen to="/utilization">アサイン・稼働</Screen>
                  {
                    ' は、予定（アサイン）に対して実績がどれだけ消化されたかを見る画面です。直接／間接の比率もここに出ます。'
                  }
                </>
              ),
            },
            {
              term: '収支・資産化',
              value: (
                <>
                  <Screen to="/capitalization">収支・資産化</Screen>
                  {' は、工数を金額に置き換えて、資産計上額と粗利を見る画面です。'}
                </>
              ),
            },
          ]}
        />
        <P>
          {
            'どちらも月単位です。日ごとの推移は持っていません（管理する粒度を月までに留めているためです）。'
          }
        </P>
      </HelpSection>

      <HelpSection heading="直接工数と間接工数">
        <P>
          {'直接／間接は、記録したときではなく'}
          <Strong>集計するとき</Strong>
          {
            'に、プロジェクトの種別を見て数えます。種別の「直接工数として数える」が有効なら直接、無効なら間接です。'
          }
        </P>
        <P>
          {
            'そのため、プロジェクトの種別を後から付け替えると過去の月の比率も動きます。意図した挙動です。締めた月の数字を動かしたくないときは、種別の付け替えを避けてください。'
          }
        </P>
      </HelpSection>

      <HelpSection heading="資産化される条件">
        <P>{'次の 2 つが同時に成り立つ工数だけが資産計上されます。'}</P>
        <Bullets
          items={[
            'プロジェクトが資産化の対象になっている',
            <>
              {'その記録のカテゴリが '}
              <Code>development</Code>
              {' である'}
            </>,
          ]}
        />
        <P>
          {
            'この判定は記録した時点で確定し、あとから条件が変わっても遡りません。プロジェクトの資産化設定を切り替えても、すでに記録済みの工数は書き換わりません。締めた月の数字が後から動かないようにするためです。'
          }
        </P>
        <Note label="訂正すると変わる">
          <P>
            {
              '記録のカテゴリを訂正すると、その記録の資産化区分だけは計算し直されます。経路は問いません（AI から直しても '
            }
            <Screen to="/time-logs">工数明細</Screen>
            {' から直しても同じです）。'}
          </P>
        </Note>
      </HelpSection>

      <HelpSection heading="単価の決まり方">
        <P>
          {
            '金額は工数に単価を掛けて出します。単価は上から順に探し、最初に見つかったものを使います。'
          }
        </P>
        <Terms
          rows={[
            {
              term: '原価単価',
              value: 'アサインごとの個別単価 → その人の単価 → 組織の既定単価',
            },
            {
              term: '売上単価',
              value: 'その人の単価 → 組織の既定単価',
            },
          ]}
        />
        <P>
          {
            '全員分を登録しなくても、組織の既定単価さえ入っていれば金額は集計されます。特定の案件だけ単価が違う場合は、アサインに個別単価を入れてください。'
          }
        </P>
        <Note label="売上単価がどこにも入っていないとき">
          <P>
            {
              'その人の単価も組織の既定単価も入っていない場合、想定売上と粗利は数字を出さずに「単価未設定」と表示します。0 円として計算すると、実際には黒字の案件まで赤字に見えてしまうためです。一部のメンバーにだけ単価が入っている場合も、そのままでは必ず少なめの数字になるので「一部未設定」として出しません。'
            }
          </P>
          <P>
            {
              '請求可能な工数がまったく無いプロジェクト（自社プロダクトの開発など）は「対象外」と表示します。この場合の売上 0 は正しく、粗利という指標がその行に無いだけです。原価と資産計上額は、どちらの場合もそのまま出ます。'
            }
          </P>
        </Note>
      </HelpSection>

      <HelpSection heading="見える範囲">
        <P>
          <Code>mcp</Code>
          {
            ' のロールの人には、稼働レポートで自分の行だけが返ります。合計もその行から計算されるので、差分から他の人の稼働が分かることはありません。'
          }
        </P>
        <P>
          {'プランとロールの対応は '}
          <SeeAlso slug="plans-and-seats">プランとシート</SeeAlso>
          {' にあります。'}
        </P>
      </HelpSection>
    </>
  );
}
